/**
 * @typedef {object} ObjectType
 * @property {string} name - название-идентификатор объекта, возможные значения:
	 <li>Жилая: room, bed, flat</li>
	 <li>Загородная: house, houseShare, townhouse, land</li>
	 <li>Нежилая: office, shoppingArea, warehouse, freeAppointmentObject, publicCatering, garage, industry, car_care, business, building, householdServices, legalAddress</li>
	 <li>Undefined, если выбрана категория целиком (м.б. у коммерческой в выдаче по БЦ)</li>
 * @property {string} category - категория недвижимости: flat/suburban/commercial
 * @property {string} filterParamName - параметр фильтра, который отвечает за данный объект
 * @property {number} filterParamValue - значение параметра filterParamName
 * @property {string} text - русскоязычное название объекта
 * @property {?string[]} restrictTo - для каких типов сделки разрешён данный объект (например, ['sale', 'rentLong']). Если не указано, то разрешён для всех.
 */

/**
 * @typedef {object} ObjectCategory
 * @property {string} id - flat/suburban/commercial
 * @property {string} name - русскоязычное название категории
 * @property {ObjectType[]} items - объекты, входящие в категорию
 * @property {string} defaultItem - имя объекта, который выбирается по умолчанию (например, flat)
 * @property {string} filterParamName - фильтр, в который сохраняется набор объектов этой категории
 */

import _ from 'lodash';

export const OBJECT_TYPES = {
	FLAT: 'flat',
	ROOM: 'room',
	BED: 'bed',
	FLATSHARE: 'flatShare',
	HOUSE: 'house',
	HOUSESHARE: 'houseShare',
	TOWNHOUSE: 'townhouse',
	LAND: 'land',
	OFFICE: 'office',
	SHOPPING_AREA: 'shoppingArea',
	WAREHOUSE: 'warehouse',
	FREE_APPOINTMENT: 'freeAppointmentObject',
	PUBLIC_CATERING: 'publicCatering',
	GARAGE: 'garage',
	INDUSTRY: 'industry',
	CARCARE: 'carCare',
	BUSINESS: 'business',
	BUILDING: 'building',
	HOUSEHOLD_SERVICES: 'householdServices',
};

angular.module('cian.filter')
	.factory('FilterObjectTypeService', function() {
		const service = this;

		const DEAL_TYPES = {
			SALE: 'sale',
			RENT : {
				LONG: 'rentLong',
				SHORT: 'rentShort',
			}
		};

		const objectTypesByCategory = {
			flat: {
				name: 'Жилая',
				filterParamName: 'room',
				defaultItem: 'flat',
				items: {
					[OBJECT_TYPES.FLAT]: {
						text: 'Квартира',
						title: 'Квартиру',
					},
					[OBJECT_TYPES.ROOM]: {
						text: 'Комната',
						title: 'Комнату',
						filterParamValue: 0,
					},
					[OBJECT_TYPES.BED]: {
						text: 'Койко-место',
						filterParamValue: 10,
						restrictTo: [DEAL_TYPES.RENT.LONG, DEAL_TYPES.RENT.SHORT],
					},
					[OBJECT_TYPES.FLATSHARE]: {
						text: 'Доля',
						title: 'Долю',
						filterParamValue: 8,
						restrictTo: [DEAL_TYPES.SALE],
					}
				}
			},
			suburban: {
				name: 'Загородная',
				filterParamName: 'object_type',
				defaultItem: 'house',
				items: {
					[OBJECT_TYPES.HOUSE]: {
						text: 'Дом',
						filterParamValue: 1,
					},
					[OBJECT_TYPES.HOUSESHARE]: {
						text: 'Часть дома',
						filterParamValue: 2,
						restrictTo: [DEAL_TYPES.SALE, DEAL_TYPES.RENT.LONG],
					},
					[OBJECT_TYPES.TOWNHOUSE]: {
						text: 'Таунхаус',
						filterParamValue: 4,
						restrictTo: [DEAL_TYPES.SALE, DEAL_TYPES.RENT.LONG],
					},
					[OBJECT_TYPES.LAND]: {
						text: 'Участок',
						filterParamValue: 3,
						restrictTo: [DEAL_TYPES.SALE, DEAL_TYPES.RENT.LONG],
					}
				}
			},
			commercial: {
				name: 'Коммерческая',
				filterParamName: 'office_type',
				// Нет дефолтного, т.к. коммерческая может быть выбрана целиком
				restrictTo: [DEAL_TYPES.SALE, DEAL_TYPES.RENT.LONG],
				items: {
					[OBJECT_TYPES.OFFICE]: {
						text: 'Офис',
						filterParamValue: 1,
					},
					[OBJECT_TYPES.SHOPPING_AREA]: {
						text: 'Торговая площадь',
						title: 'Торг. площадь',
						filterParamValue: 2,
					},
					[OBJECT_TYPES.WAREHOUSE]: {
						text: 'Склад',
						filterParamValue: 3,
					},
					[OBJECT_TYPES.FREE_APPOINTMENT]: {
						text: 'ПСН',
						filterParamValue: 5,
					},
					[OBJECT_TYPES.PUBLIC_CATERING]: {
						text: 'Общепит',
						filterParamValue: 4,
					},
					[OBJECT_TYPES.GARAGE]: {
						text: 'Гараж',
						filterParamValue: 6,
					},
					[OBJECT_TYPES.INDUSTRY]: {
						text: 'Производство',
						title: 'Производ.',
						filterParamValue: 7,
					},
					[OBJECT_TYPES.CARCARE]: {
						text: 'Автосервис',
						filterParamValue: 9,
					},
					[OBJECT_TYPES.BUSINESS]: {
						text: 'Готовый бизнес',
						title: 'Готов. бизнес',
						filterParamValue: 10,
					},
					[OBJECT_TYPES.BUILDING]: {
						text: 'Здание',
						filterParamValue: 11,
					},
					[OBJECT_TYPES.HOUSEHOLD_SERVICES]: {
						text: 'Бытовые услуги',
						title: 'Быт. услуги',
						filterParamValue: 12,
					}
				}
			}
		};

		// add name, category and filterParamName info to each objectType
		angular.forEach(objectTypesByCategory, function(category, categoryName) {
			category.id = categoryName;

			angular.forEach(category.items, function(objectType, objectName) {
				objectType.name = objectName;
				objectType.category = categoryName;
				objectType.filterParamName = category.filterParamName;
			});
		});

		var objectTypeByName = _.transform(objectTypesByCategory, function(memo, objectTypes, category) {
			return angular.forEach(objectTypes.items, (objectType, objectTypeName) => {
				memo[objectTypeName] = objectType;
			});
		}, {});

		/**
		 * @param restrictions
		 * @param {string} dealType  - rent/sale
		 * @param {?boolean} isShortTerm
		 * @return {boolean}
		 */
		function isAllowed(restrictions, dealType, isShortTerm) {
			if (!restrictions) return true;

			const fullDealType = dealType === 'sale' ? DEAL_TYPES.SALE :
				(isShortTerm ? DEAL_TYPES.RENT.SHORT : DEAL_TYPES.RENT.LONG);

			return _.includes(restrictions, fullDealType);
		}

		/**
		 * Определяет выбранные объекты по состоянию фильтра
		 * @memberOf FilterObjectTypeService
		 * @param {string} category
		 * @param {object} filter
		 * @return {ObjectType[]}
		 */
		service.determineObjectTypes = (category, filter) => {
			const objectTypeCategory = objectTypesByCategory[category];
			const found = _.filter(objectTypeCategory.items, objectType => _.includes(
				filter[objectTypeCategory.filterParamName].value,
				objectType.filterParamValue
			));

			// Удалось определить
			if (found.length) return found;

			// Если есть дефолтное, то возвращаем его
			if (objectTypeCategory.defaultItem) {
				return [objectTypeCategory.items[objectTypeCategory.defaultItem]];
			}

			return [];
		};

		/**
		 * @memberOf FilterObjectTypeService
		 * @param {string} objectTypeName
		 * @return {ObjectType}
		 */
		service.getObjectTypeByName = objectTypeName => objectTypeByName[objectTypeName];

		/**
		 * @memberOf FilterObjectTypeService
		 * @param {string} dealType - rent/sale
		 * @param {?boolean} isShortTerm
		 * @return {ObjectCategory[]}
		 */
		service.getObjectCategoriesForDeal = (dealType, isShortTerm) => {
			// Фильтруем категории
			const filteredCategories = _.filter(objectTypesByCategory,
				cat => isAllowed(cat.restrictTo, dealType, isShortTerm)
			);

			// Фильтруем типы объектов
			return _.map(filteredCategories, cat => ({
				...cat,
				items: _.filter(cat.items, item => isAllowed(item.restrictTo, dealType, isShortTerm)),
			}));
		};

		/**
		 * Объекты, которые доступны для типа сделки
		 * @memberOf FilterObjectTypeService
		 * @param {string} dealType - rent/sale
		 * @param {?boolean} isShortTerm
		 * @return {ObjectType[]}
		 */
		service.getObjectTypesForDeal = (dealType, isShortTerm) => {
			const categories = _.map(
				service.getObjectCategoriesForDeal(dealType, isShortTerm), 'items'
			);
			return _.flatten(categories);
		};

		return service;
	});



// WEBPACK FOOTER //
// ./src/components/filter/src/scripts/cian.filter.object-type.service.js